'use client';

import { Link } from "@/i18n/routing";

export enum NOTIFICATION_TYPES {
    LOAD_DOCUMENT = 'load_document',
    APPROVE_DOCUMENT = 'approve_document',
    REJECT_DOCUMENT = 'reject_document',
    DELETE_DOCUMENT = 'delete_document',
    CHANGE_CUSTOMER_VERIFICATION_STATUS = 'change_customer_verification_status',
    CHANGE_CUSTOMER_TRANSACTION_STATUS = 'change_customer_transaction_status',
    CREATE_CUSTOMER_INVOICE = 'create_customer_invoice',
    CHANGE_CUSTOMER_INVOICE = 'change_customer_invoice',
    DELETE_CUSTOMER_INVOICE = 'delete_customer_invoice',
    PAY_CUSTOMER_INVOICE = 'pay_customer_invoice',
    DELETE_CHAT = 'delete_chat',
    CUSTOMER_CLOSE_CHAT = 'customer_close_chat',
    EMPLOYEE_CLOSE_CHAT = 'employee_close_chat',
    NEW_CHAT_MESSAGE = 'new_chat_message',
    IMPORT_DUPLICATE = 'import_duplicate',
    INTEGRATION_DUPLICATE = 'integration_duplicate'
};

export interface NOTIFICATION_MESSAGE {
    [key: string]: string;
}

const stringReplacer = (string: string, data: NOTIFICATION_MESSAGE): string => {
    return string?.replace(/\{(\w+)\}/g, (_, key) => data[key] ?? `{${key}}`);
};

export const replacer = (template: string | ((data: NOTIFICATION_MESSAGE) => React.ReactNode), data: NOTIFICATION_MESSAGE): string | React.ReactNode => {
    if (typeof template === 'string') return stringReplacer(template, data);
    if (typeof template === 'function') return template(data);
};

export const notification_templates = {
    ru: {
        load_document: '{employee_name} загрузил Вам документ {document_name}',
        approve_document: 'Ваш документ {document_name} одобрен',
        reject_document: 'Ваш документ {document_name} отклонен',
        delete_document: 'Ваш документ {document_name} удален',
        create_deposit: '{first_name} {last_name} создал депозит на сумму {amount} {asset_code}',
        change_customer_verification_status: 'Вам присвоен статус {verification_status}',
        change_customer_transaction_status: 'Вашей транзакции по уникальному номеру #{transaction_id} был изменён статус на {transaction_status_name}',
        create_customer_invoice: (data: NOTIFICATION_MESSAGE) => (
            <>
                Вам был выставлен счёт:<br />{data.payout_amount} {data.payout_asset_currency_code} <br />{data.currency_amount} {data.currency_currency_code} <br /> {data.usd_amount}
            </>
        ),
        change_customer_invoice: 'Счёт с уникальным идентификатором #{invoice_id} был изменён',
        delete_customer_invoice: 'Счёт с уникальным идентификатором #{invoice_id} был удален',
        pay_customer_invoice: 'Клиент {customer_name} оплатил счёт на сумму {amount} {payout_asset_currency_code}',
        delete_chat: 'Чат {subject} был удалён',
        customer_close_chat: 'Чат {subject} был закрыт',
        employee_close_chat: 'Чат {subject} был закрыт',
        new_chat_message: (data: NOTIFICATION_MESSAGE) => (
            <>
                Новое сообщение в чате{' '}
                <Link className='text-brand-600 hover:underline' href={`/chats/${data.chat_id}`}>
                    (#{data.chat_id} | {data.subject})
                </Link>
            </>
        ),
        import_duplicate: 'Найден дубль {duplicate_name} {duplicate_phone} в импорте с идентификатором #{import_id}',
        integration_duplicate: 'При интеграции с {source} найден дубль {duplicate_name} {duplicate_phone}',
    },

    en: {
        load_document: '{employee_name} uploaded a document for you: {document_name}',
        approve_document: 'Your document {document_name} has been approved',
        reject_document: 'Your document {document_name} has been rejected',
        delete_document: 'Your document {document_name} has been deleted',
        create_deposit: '{first_name} {last_name} created a deposit in the amount of {amount} {asset_code}',
        change_customer_verification_status: 'You have been assigned the status {verification_status}',
        change_customer_transaction_status: 'The status of your transaction with unique number #{transaction_id} has been changed to {transaction_status_name}',
        create_customer_invoice: (data: NOTIFICATION_MESSAGE) => (
            <>
                A new invoice has been issued for you:<br />{data.payout_amount} {data.payout_asset_currency_code} <br /> {data.currency_amount} {data.currency_currency_code}<br /> {data.usd_amount}
            </>
        ),
        change_customer_invoice: 'The invoice with unique ID #{invoice_id} has been updated',
        delete_customer_invoice: 'The invoice with unique ID #{invoice_id} has been deleted',
        pay_customer_invoice: 'Customer {customer_name} has paid the invoice for {amount} {payout_asset_currency_code}',
        delete_chat: 'Chat {subject} has been deleted',
        customer_close_chat: 'Chat {subject} has been closed',
        employee_close_chat: 'Chat {subject} has been closed',
        new_chat_message: (data: NOTIFICATION_MESSAGE) => (
            <>
                New message in ticket{' '}
                <Link className='text-brand-600 hover:underline' href={`/chats/${data.chat_id}`}>
                    (#{data.chat_id} | {data.subject})
                </Link>
            </>
        ),
        import_duplicate: 'Duplicate {duplicate_name} {duplicate_phone} found in import with ID #{import_id}',
        integration_duplicate: 'Duplicate {duplicate_name} {duplicate_phone} found during integration with {source}',
    },
};
